import React, { useState, useEffect, Fragment, useCallback } from "react";
import { withRouter } from 'react-router-dom';
import "./../View.css";
import "./../Grid.css";
import "./../Form.css";
import "./../App.css";
import withMenuPermission from "../widgets/withMenuPermission";
import ViewUtils from '../ViewUtils';
import LoadingIndicatorLogin from '../LoadingIndicatorLogin';
import { ViewContainer } from "../View";
import { 
    Paper, TableContainer, TableRow, TextField, 
    Button, MenuItem, Box, CircularProgress, Typography,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle 
} from '@material-ui/core';
import AlertItem from "../widgets/AlertItem";

const utils = new ViewUtils();

const APPExportReport = (props) => {
    // --- State ---
    const [isLoading, setIsLoading] = useState(true);
    const [queueData, setQueueData] = useState(null);
    const [APPSnapshotID, setAPPSnapshotID] = useState(0);
    const [lookupArray, setLookupArray] = useState([]);
    const [message, setMessage] = useState("");
    const [messageStyle, setMessageStyle] = useState("");
    const [confirmOpen, setConfirmOpen] = useState(false); // Dialog state

    // --- Status Constants ---
    const STATUS_COMPLETED = 3;
    const STATUS_FAILED = 4;

    const isProcessing = queueData && 
                         queueData.APPReportExportQueueStatusID && 
                         ![STATUS_COMPLETED, STATUS_FAILED].includes(queueData.APPReportExportQueueStatusID.Id);

    const getStatusColor = () => {
        const id = (queueData && queueData.APPReportExportQueueStatusID) ? queueData.APPReportExportQueueStatusID.Id : null;
        if (id === STATUS_COMPLETED) return "#4caf50";
        if (id === STATUS_FAILED) return "#f44336";
        if (isProcessing) return "#ff9800";
        return "inherit";
    };

    // --- API Logic ---
    const getFormHead = useCallback((showGlobalLoader = true) => {
        if (APPSnapshotID === 0) {
            setQueueData(null);
            setIsLoading(false);
            return;
        }
        if (showGlobalLoader) setIsLoading(true);
        let url = "api/sims/APPDashboard/GetFindQueue?APPSnapshotId=" + APPSnapshotID;
        utils.invokeUrl(url, (response) => {
            setQueueData(response);
            setIsLoading(false);
        }, null, {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'Authorization': "Bearer " + localStorage.getItem("token")
            }
        });
    }, [APPSnapshotID]);

    const getLookup = () => {
        let url = "api/sims/APPDashboard/GetAPPSnapshotByFinancialYear?FinancialYearId=" + props.FinancialYearID;
        utils.invokeUrl(url, (response) => {
            setLookupArray(response);
            setIsLoading(false);
        }, null, {
            method: 'GET',
            headers: { 'Authorization': "Bearer " + localStorage.getItem("token") }
        });
    };

    // --- Effects ---
    useEffect(() => { getLookup(); }, [props.FinancialYearID]);
    useEffect(() => { 
        if (APPSnapshotID !== 0) getFormHead(true); 
        else setQueueData(null);
    }, [APPSnapshotID, getFormHead]);

    useEffect(() => {
        let interval;
        if (isProcessing && APPSnapshotID !== 0) {
            interval = setInterval(() => getFormHead(false), 5000);
        }
        return () => { if (interval) clearInterval(interval); };
    }, [isProcessing, APPSnapshotID, getFormHead]);

    // --- Handlers ---
    const handleReset = () => {
        setAPPSnapshotID(0);
        setQueueData(null);
        setMessage("");
        setMessageStyle("");
    };

    const handleConfirmOpen = () => setConfirmOpen(true);
    const handleConfirmClose = () => setConfirmOpen(false);

    const handleGenerateReport = () => {
        handleConfirmClose(); // Close dialog immediately
        if (APPSnapshotID === 0) return;
        setIsLoading(true);
        let url = "api/sims/APPDashboard/APPReportExportQueueStart?APPSnapshotId=" + APPSnapshotID;
        utils.invokeUrl(url, (response) => {
            if (response.MessageType === "SUCCESS") {
                getFormHead();
            } else {
                setIsLoading(false);
                setMessage(response.Message);
                setMessageStyle(response.MessageType);
            }
        }, null, {
            method: 'GET',
            headers: { 'Authorization': "Bearer " + localStorage.getItem("token") }
        });
    };

    const handleFileDownload = () => {
        if (!queueData || !queueData.Document || !queueData.Document.Base64) return;
        const binaryString = window.atob(queueData.Document.Base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes.buffer], { type: queueData.Document.Type });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = queueData.Document.Name;
        link.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Fragment>
            <LoadingIndicatorLogin show={isLoading} />
            {!isLoading && (
                <ViewContainer topPadding="0" title="">
                    <AlertItem message={message} alertStyle={messageStyle} />
                    <br />
                    <TableContainer component={Paper}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <tbody>
                                <TableRow>
                                    <td style={{ padding: '16px', width: '20%' }}><b>Quarter</b></td>
                                    <td style={{ padding: '16px' }}>
                                        <TextField 
                                            select variant="outlined" size="small" fullWidth
                                            value={APPSnapshotID} 
                                            onChange={(e) => setAPPSnapshotID(e.target.value)}
                                        >
                                            <MenuItem value={0}>Select Quarter</MenuItem>
                                            {lookupArray && lookupArray.map((item) => (
                                                <MenuItem value={item.Id} key={item.Id}>{item.Description}</MenuItem>
                                            ))}
                                        </TextField>
                                    </td>
                                </TableRow>

                                <TableRow>
                                    <td style={{ padding: '16px' }}><b>Status</b></td>
                                    <td style={{ padding: '16px' }}>
                                        <Box display="flex" alignItems="center">
                                            <TextField 
                                                disabled fullWidth variant="outlined" size="small"
                                                value={(queueData && queueData.APPReportExportQueueStatusID) ? queueData.APPReportExportQueueStatusID.Description : ""} 
                                                inputProps={{ 
                                                    style: { 
                                                        color: getStatusColor(), 
                                                        fontWeight: 'bold',
                                                        WebkitTextFillColor: getStatusColor() 
                                                    } 
                                                }}
                                            />
                                            {isProcessing && (
                                                <Box ml={2} display="flex" alignItems="center" minWidth="120px">
                                                    <CircularProgress size={20} style={{ color: '#ff9800' }} />
                                                    <Typography variant="caption" style={{ marginLeft: '8px', color: '#ff9800' }}>
                                                        Updating...
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </td>
                                </TableRow>

                                <TableRow>
                                    <td style={{ padding: '16px' }}><b>Last Ran</b></td>
                                    <td style={{ padding: '16px' }}>
                                        <TextField 
                                            disabled fullWidth variant="outlined" size="small"
                                            value={(queueData && queueData.DateUpdated) ? queueData.DateUpdated : "N/A"} 
                                        />
                                    </td>
                                </TableRow>
                            </tbody>
                        </table>
                    </TableContainer>

                    <Box mt={3} display="flex" style={{ gap: '10px' }}>
                        <Button variant="contained" color="primary" disabled={!(queueData && queueData.Document)} onClick={handleFileDownload}>Download File</Button>
                        <Button variant="outlined" color="primary" disabled={isProcessing || APPSnapshotID === 0} onClick={handleConfirmOpen}>Generate Report</Button>
                        <Button variant="outlined" disabled={isProcessing || APPSnapshotID === 0} onClick={() => getFormHead(true)}>Refresh</Button>
                        <Button variant="outlined" color="secondary" onClick={handleReset}>Reset</Button>
                    </Box>

                    {/* Confirmation Dialog */}
                    <Dialog open={confirmOpen} onClose={handleConfirmClose}>
                        <DialogTitle>{"Generate New Report?"}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>
                                This will queue a new report export for the selected quarter. 
                                It may take a few minutes to complete. Do you want to continue?
                            </DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleConfirmClose} color="primary">Cancel</Button>
                            <Button onClick={handleGenerateReport} color="primary" autoFocus variant="contained">Confirm</Button>
                        </DialogActions>
                    </Dialog>

                </ViewContainer>
            )}
        </Fragment>
    );
};

export default withRouter(withMenuPermission(APPExportReport));